import React, { useRef, useState } from 'react';
import { Shape, PinPath, Point } from './Types';
import { useStringArtAlgorithm } from './hooks/useStringArtAlgorithm';
import ShowTargetButton from './components/ShowTargetButton';
import TechnicalInfo from './components/TechnicalInfo';
import { MonteCarloDescentAlgorithm } from './MonteCarloDescentAlgorithm';
import { Target } from './Target';
import { PathPreview } from './components/PathPreview';

interface RunProgressProps {
  imageUrl: string;
  shape: Shape;
  invertImage: boolean;
  maskSize: number;
  pathLength: number;
  numberOfPins: number;
}

const RunProgress: React.FC<RunProgressProps> = ({
  imageUrl,
  shape,
  invertImage,
  maskSize,
  pathLength,
  numberOfPins,
}) => {
  const [currentError, setCurrentError] = useState(0);
  const [showingTarget, setShowingTarget] = useState(false);
  const [iterationsPerSecond, setIterationsPerSecond] = useState(0);
  const [iterationTime, setIterationTime] = useState(0);
  const [maxError, setMaxError] = useState(0);
  const [errorVelocity, setErrorVelocity] = useState(0);

  const imageRef = useRef<HTMLImageElement>(null);
  const [targetImageData, setTargetImageData] = useState<ImageData | null>(null);
  const [currentPath, setCurrentPath] = useState<PinPath | null>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const pointsRef = useRef<Point[]>([]);

  const handlePointsUpdate = (newPoints: Point[]) => {
    pointsRef.current = newPoints;
    setPoints(newPoints);
  };
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });

  const { runAlgorithm } = useStringArtAlgorithm({
    updateError: setCurrentError,
    updateIterationsPerSecond: setIterationsPerSecond,
    updateRunningTime: setIterationTime,
    updateMaxError: setMaxError,
    updateErrorVelocity: setErrorVelocity,
    updateTargetImageData: setTargetImageData,
    updateCurrentPath: setCurrentPath,
    updatePoints: handlePointsUpdate,
  });

  const onImageLoad = () => {
    const image = imageRef.current;
    if (!image) {
      return;
    }

    const target = new Target(image, 400, invertImage, shape, maskSize);
    setCanvasDimensions({ width: target.width, height: target.height });

    const scoringFunction = (path: PinPath) => target.computePathDiff(path, pointsRef.current);
    const algorithm = new MonteCarloDescentAlgorithm(scoringFunction, pathLength);

    runAlgorithm(shape, algorithm, target, numberOfPins, pathLength).catch((error) => {
      console.error('Algorithm error:', error);
    });
  };

  return (
    <div className="text-center px-4 py-8 max-w-full overflow-hidden">
      <img ref={imageRef} src={imageUrl} className="hidden" onLoad={onImageLoad} />

      <h4 className="text-base md:text-lg font-semibold mb-4">Current Approximation: {currentError.toFixed(2)}</h4>
      <div className="flex justify-center mb-6">
        <PathPreview
          path={currentPath || undefined}
          points={points}
          showTarget={showingTarget}
          targetImageData={targetImageData || undefined}
          width={canvasDimensions.width}
          height={canvasDimensions.height}
        />
      </div>

      <ShowTargetButton onShowTarget={setShowingTarget} />

      <TechnicalInfo
        iterationsPerSecond={iterationsPerSecond}
        iterationTime={iterationTime}
        maxError={maxError}
        errorVelocity={errorVelocity}
        currentPathLength={currentPath?.pinIndices.length || 0}
        numberOfPins={numberOfPins}
      />
    </div>
  );
};

export default RunProgress;
